import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Button, Grid, MenuItem, Box, FormControl, InputLabel, Select, FormHelperText } from '@mui/material';
import { DepartmentFormData } from '../../types/department.types';

interface DepartmentFormProps {
  initialData?: Partial<DepartmentFormData>;
  onSubmit: (data: DepartmentFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const departmentTypes = [
  { value: 'CLINICAL', label: 'Clinical' },
  { value: 'ADMINISTRATIVE', label: 'Administrative' },
  { value: 'SUPPORT', label: 'Support' },
  { value: 'SERVICE', label: 'Service' },
];

const departmentSubTypes = [
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'SURGICAL', label: 'Surgical' },
  { value: 'DIAGNOSTIC', label: 'Diagnostic' },
  { value: 'NURSING', label: 'Nursing' },
];

export const DepartmentForm: React.FC<DepartmentFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const { control, handleSubmit, formState: { errors } } = useForm<DepartmentFormData>({
    defaultValues: {
      name: initialData?.name || '',
      code: initialData?.code || '',
      description: initialData?.description || '',
      deptType: initialData?.deptType || undefined,
      subType: initialData?.subType || undefined,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Department Name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="code"
            control={control}
            rules={{ required: 'Code is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Department Code"
                fullWidth
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="deptType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.deptType}>
                <InputLabel>Department Type</InputLabel>
                <Select
                  {...field}
                  label="Department Type"
                  value={field.value || ''}
                >
                  {departmentTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.deptType && (
                  <FormHelperText>{errors.deptType.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="subType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.subType}>
                <InputLabel>Sub Type</InputLabel>
                <Select
                  {...field}
                  label="Sub Type"
                  value={field.value || ''}
                >
                  {departmentSubTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.subType && (
                  <FormHelperText>{errors.subType.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                rows={3}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Department'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};
